import { motion } from 'framer-motion';
import { ExternalLink, FlaskConical, Car, Globe, Dumbbell, Handshake, ArrowRight } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';

const containerVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const peptideExperience = [
  {
    stat: '7',
    label: 'Emirates served',
    desc: 'Cold-chain delivery across every UAE emirate, from Dubai to Fujairah.',
  },
  {
    stat: '99.8%+',
    label: 'Purity standard',
    desc: 'HPLC-verified research peptides with independent batch testing.',
  },
  {
    stat: 'COD',
    label: 'Cash on delivery',
    desc: 'Built trust from day one with pay-on-delivery across the UAE.',
  },
  {
    stat: 'WhatsApp',
    label: 'Advisor support',
    desc: 'Real-time guidance from people who actually understand peptides.',
  },
];

const ventures = [
  {
    id: 'peptide-aura',
    icon: FlaskConical,
    title: 'Peptide Aura',
    subtitle: 'UAE Cold-Chain Peptides',
    description:
      'Co-founder of Peptide Aura. HPLC-tested research peptides delivered across all 7 Emirates with cash-on-delivery and expert WhatsApp support.',
    href: 'https://peptide-aura.store',
  },
  {
    id: 'luxury-auto',
    icon: Car,
    title: 'Luxury Auto Parts',
    subtitle: 'AMG & European Performance',
    description:
      'Premium performance parts for AMG, European, and luxury vehicles. Global sourcing network with direct-to-client concierge service.',
    href: 'https://husam-birouti-hb.netlify.app/',
  },
  {
    id: 'global-trade',
    icon: Globe,
    title: 'Global Trade & Sourcing',
    subtitle: 'Cross-Border B2B',
    description:
      'Cross-border B2B supply chain solutions. UAE hub connecting Asia, Europe, and GCC markets for specialized goods and automotive components.',
    href: 'https://husam-birouti-hb.netlify.app/',
  },
  {
    id: 'performance',
    icon: Dumbbell,
    title: 'Performance Consulting',
    subtitle: 'Gym · Recovery · Peptides',
    description:
      'Gym, recovery, and peptide optimization guidance for high-performance individuals who want results without compromise.',
    href: 'https://husam-birouti-hb.netlify.app/',
  },
  {
    id: 'partnerships',
    icon: Handshake,
    title: 'Brand Partnerships',
    subtitle: 'Lifestyle Collaborations',
    description:
      'Lifestyle, automotive, and fitness brand collaborations. Cabin crew reach + entrepreneur network across Dubai and global markets.',
    href: 'https://husam-birouti-hb.netlify.app/',
  },
];

export default function FounderPage() {
  return (
    <div className="min-h-full bg-rp-paper">
      {/* Hero header */}
      <section className="relative overflow-hidden border-b border-rp-line bg-rp-bone">
        <div className="rp-container py-16 md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="mx-auto max-w-3xl text-center"
          >
            <span className="rp-mono-mini text-rp-moss">Behind AURA</span>
            <h1 className="mt-4 font-sans text-h1 font-semibold tracking-tight text-rp-ink">
              Meet the <em className="not-italic text-rp-forest">founder</em>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-rp-moss">
              Husam Birouti built Peptide Aura to bring lab-grade research peptides to the UAE — no compromises, no middlemen, just real products and real support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bio section with image */}
      <section className="rp-container py-12 md:py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16 items-start">
          {/* Portrait / intro card */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={containerVariants}
            className="rounded-2xl border border-rp-line bg-rp-bone p-6 md:p-8"
          >
            <div className="aspect-square w-full overflow-hidden rounded-xl bg-rp-line">
              <img
                src="/images/founder/husam_hero.jpeg"
                alt="Husam Birouti"
                className="h-full w-full object-cover"
              />
            </div>
            <h2 className="mt-6 font-sans text-2xl font-semibold tracking-tight text-rp-ink">
              Husam Birouti
            </h2>
            <p className="mt-1 text-sm font-medium text-rp-forest">Co-founder, Peptide Aura</p>
            <p className="mt-4 text-sm leading-relaxed text-rp-moss">
              Dubai-based entrepreneur with Chinese, Thai, and Syrian roots. Cabin crew by profession, builder by obsession.
            </p>
            <a
              href="https://husam-birouti-hb.netlify.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="rp-btn mt-6 w-full bg-rp-forest text-rp-paper hover:bg-rp-forest-2"
            >
              View full portfolio
              <ExternalLink size={14} />
            </a>
          </motion.div>

          {/* Bio text focused on peptides */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={containerVariants}
          >
            <SectionHeader
              eyebrow="Peptide experience"
              title="Built for researchers, by a researcher"
              description="Husam didn't start Peptide Aura as a side hustle. He started it because he couldn't find peptide suppliers in the UAE that met his own standard."
              align="left"
            />
            <div className="space-y-4 text-sm leading-relaxed text-rp-moss">
              <p>
                After years of personal research into recovery, performance, and longevity, Husam saw a gap in the UAE market: researchers and high-performance individuals needed access to HPLC-tested peptides without waiting weeks for international shipping or guessing whether a product was real.
              </p>
              <p>
                Peptide Aura was built to fix that. From sourcing raw materials to validating batches, setting up cold-chain logistics, and building a WhatsApp-based advisory line, Husam runs the operation with the same detail he applies to his own protocols.
              </p>
              <p>
                Today Peptide Aura supplies researchers, clinicians, and athletes across all 7 Emirates — with cash-on-delivery, batch traceability, and advisors who actually understand the compounds.
              </p>
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                { label: 'Based in', value: 'Dubai, UAE' },
                { label: 'Role at AURA', value: 'Co-founder & Operations' },
                { label: 'Heritage', value: 'Chinese · Thai · Syrian' },
                { label: 'Focus', value: 'Peptide supply & research support' },
              ].map((item) => (
                <div key={item.label} className="rounded-xl border border-rp-line bg-rp-bone p-4">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-moss">{item.label}</p>
                  <p className="mt-1 text-sm font-semibold text-rp-ink">{item.value}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Peptide experience stats */}
      <section className="border-t border-rp-line bg-rp-forest text-rp-paper">
        <div className="rp-container py-12 md:py-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4"
          >
            {peptideExperience.map((item) => (
              <div key={item.label} className="text-center sm:text-left">
                <p className="font-sans text-3xl font-semibold tracking-tight text-rp-paper md:text-4xl">{item.stat}</p>
                <p className="mt-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-rp-leaf">{item.label}</p>
                <p className="mt-2 text-sm leading-relaxed text-rp-leaf-soft">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Ventures */}
      <section className="border-t border-rp-line bg-rp-bone">
        <div className="rp-container py-12 md:py-20">
          <SectionHeader
            eyebrow="Other ventures"
            title="Beyond peptides"
            description="Peptide Aura is the focus, but Husam's network spans performance, automotive, and global trade."
            align="center"
          />

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ventures.map((venture, index) => (
              <motion.a
                key={venture.id}
                href={venture.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: index * 0.08, duration: 0.4 }}
                whileHover={{ y: -4 }}
                className="group flex flex-col rounded-2xl border border-rp-line bg-rp-paper p-6 transition-shadow hover:shadow-card-hover"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-rp-forest text-rp-paper transition-colors group-hover:bg-rp-forest-2">
                  <venture.icon size={22} />
                </span>
                <h3 className="mt-5 font-sans text-lg font-semibold text-rp-ink">{venture.title}</h3>
                <p className="text-xs font-medium text-rp-forest">{venture.subtitle}</p>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-rp-moss">{venture.description}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-rp-forest transition-colors group-hover:text-rp-forest-2">
                  Learn more <ArrowRight size={14} />
                </span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="rp-container py-12 md:py-20">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="rounded-2xl bg-rp-forest p-8 text-rp-paper md:p-12"
        >
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-sans text-2xl font-semibold tracking-tight md:text-3xl">
              Questions about peptides?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-rp-leaf-soft">
              Husam and the AURA advisor team reply on WhatsApp with real guidance — not copy-paste answers.
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="https://wa.me/971565628081"
                target="_blank"
                rel="noopener noreferrer"
                className="rp-btn bg-rp-paper text-rp-forest hover:bg-rp-bone"
              >
                Message Husam on WhatsApp
                <ExternalLink size={14} />
              </a>
              <a href="/products" className="rp-btn border border-rp-leaf text-rp-paper hover:bg-rp-forest-2">
                Shop peptides
              </a>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
